/**
 * ============================================================================
 * KEIN ZOOM AUF DER SEITE - AUSSER DA, WO ER GEWOLLT IST
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (22.09.2026, mit 2 Bildschirmfotos derselben Minute)
 *   „Die mobile Version ist immernoch zoombar, was zu Propellern führt.
 *    Behebe es."
 *
 * Auf seinen beiden Bildern steht dasselbe Menü zur selben Uhrzeit: einmal
 * ganz, einmal nach links verrutscht und am Rand abgeschnitten. Das ist kein
 * Fehler im Aufbau der Seite, das ist eine gezoomte und verschobene Ansicht.
 *
 * ----------------------------------------------------------------------------
 * 3 RIEGEL, WEIL ES 3 WEGE HINEIN GIBT
 * ----------------------------------------------------------------------------
 *   1. Die Angabe im Kopf der Seite (`index.html`). **iOS Safari übergeht
 *      `user-scalable=no` seit iOS 10** - auf Android hilft sie, auf dem
 *      iPhone nicht.
 *   2. Die Fingergeste. Dafür ist diese Datei da.
 *   3. Das Antippen eines Eingabefeldes. Ist die Schrift darin kleiner als 16
 *      Pixel, zoomt iOS von selbst hinein und bleibt danach gezoomt. **Das
 *      ist der Weg, den niemand als Geste erkennt**, und mit 14 Pixeln
 *      Grundschrift war er hier offen. Der Riegel steht im Stylesheet.
 *
 * ----------------------------------------------------------------------------
 * WARUM NICHT EINFACH ALLES ABFANGEN
 * ----------------------------------------------------------------------------
 * Dieses Haus hat Flächen, auf denen der Zoom AUSDRÜCKLICH gewollt ist:
 *
 *   ORIGINALTEXT MARVIN (15.09.2026)
 *     „bei dem öffnen der fotos brauchen wir keinen Rahmen. Es reicht das
 *      reine Foto, dann mit Funktion links/rechts wischen und Zoom mit den
 *      Fingern fehlt noch. Bei den pdf Dateien fehlt auch der Zoom."
 *
 * Ein Riegel, der die Fotoansicht mitnimmt, nimmt eine Zusage zurück, die 7
 * Tage alt ist. Deshalb fragt jeder Abbruch hier zuerst, ob die Berührung
 * INNERHALB einer solchen Fläche liegt - `.zoomflaeche` und `.kamerabuehne`
 * tragen ihre eigene Geste und ihr eigenes `touch-action: none`.
 *
 * ----------------------------------------------------------------------------
 * UND WARUM ES NICHT IM BAUTEIL HÄNGT
 * ----------------------------------------------------------------------------
 * React hängt `touchstart` und `touchmove` PASSIV am Wurzelknoten an, und ein
 * passiver Zuhörer darf nicht abbrechen: der Ruf wird stillschweigend
 * übergangen, ohne Fehler und ohne Meldung in der Konsole. Dieses Haus hat
 * das am 02.09.2026 an der Tiefbaukarte bezahlt. Hier hängt alles von Hand
 * am Fenster, mit `{ passive: false }` und `capture`.
 */

/** Flächen, auf denen der Zoom gewollt ist und die ihn selbst führen. */
const EIGENE_GESTE = '.zoomflaeche, .kamerabuehne, [data-zoom-erlaubt]';

/** Liegt diese Berührung auf einer Fläche mit eigener Geste? */
function eigeneGeste(ziel: EventTarget | null): boolean {
  return ziel instanceof Element && ziel.closest(EIGENE_GESTE) !== null;
}

/**
 * Hängt die Riegel ans Fenster. Gibt eine Funktion zurück, die sie wieder
 * abnimmt - gebraucht wird sie nur von den Prüfständen, im Betrieb läuft es
 * bis zum Schliessen des Reiters.
 */
export function keinZoom(fenster: Window = window): () => void {
  const abnehmen: (() => void)[] = [];
  const an = (
    name: string, hoerer: (e: any) => void,
    ziel: EventTarget = fenster.document
  ) => {
    ziel.addEventListener(name, hoerer, { passive: false, capture: true });
    abnehmen.push(() => ziel.removeEventListener(name, hoerer, true));
  };

  /* 1. DIE SAFARI-GESTE. „gesturestart" ist Apples eigenes Ereignis für den
        Kneifzoom und das EINZIGE, mit dem er sich auf dem iPhone abbrechen
        lässt. Es steht in keinem Standard - deshalb `any` und kein Typ. */
  for (const name of ['gesturestart', 'gesturechange', 'gestureend']) {
    an(name, (e: Event) => {
      if (eigeneGeste(e.target)) return;
      e.preventDefault();
    });
  }

  /* 2. ZWEI FINGER AUF EINER GEWÖHNLICHEN FLÄCHE. Der Weg für die Browser,
        die Apples Ereignisse nicht kennen (Android, Firefox). EIN Finger
        wird NIE abgebrochen: das wäre das Rollen der Seite, und eine Liste,
        die sich nicht schieben lässt, ist schlimmer als eine, die zoomt. */
  an('touchmove', (e: TouchEvent) => {
    if (e.touches.length < 2) return;
    if (eigeneGeste(e.target)) return;
    e.preventDefault();
  });

  /* 3. DER DOPPELTIPP. `touch-action: manipulation` im Stylesheet erledigt
        ihn auf den meisten Geräten; auf älteren iOS-Fassungen nicht. Der
        Riegel misst den Abstand zum vorigen Tipp: unter 300 ms ist es ein
        Doppeltipp, darüber sind es 2 Tipps, und der 2. muss durch - sonst
        verschluckt diese Zeile jeden schnellen Klick auf einen Haken. */
  let letzter = 0;
  an('touchend', (e: TouchEvent) => {
    const jetzt = Date.now();
    if (jetzt - letzter < 300 && !eigeneGeste(e.target)) e.preventDefault();
    letzter = jetzt;
  });

  /* 4. DAS MAUSRAD MIT STRG UND DIE ZWEI-FINGER-GESTE AM TRACKPAD. Am
        Schreibtisch ist der Seitenzoom des Browsers ein WERKZEUG und kein
        Versehen: wer schlecht sieht, stellt ihn absichtlich ein, und
        Strg+Plus nimmt ihm niemand weg. Deshalb greift dieser Riegel NUR
        auf Geräten, die mit dem Finger bedient werden. */
  const istFinger = fenster.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  if (istFinger) {
    an('wheel', (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      if (eigeneGeste(e.target)) return;
      e.preventDefault();
    });
  }

  return () => { for (const weg of abnehmen) weg(); };
}
